/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import {useAuth} from '../../../modules/auth'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import db from '../../../../services/firebase'
import useCrispChat from 'app/Hooks/useCrispChat'
import {Link} from 'react-router-dom'
import Button from 'app/components/Button'

type Props = {
  className: string
}

const Reports: React.FC<Props> = ({className}) => {
  const {currentUser} = useAuth()
  const [totalISO, setTotalISO] = useState(0)
  const [totalSOC, setTotalSOC] = useState(0)
  const [healthyISOControlsCount, setHealthyISOControlsCount] = useState(0)
  const [healthySOCControlsCount, setHealthySOCControlsCount] = useState(0)
  const [controls, setControls] = useState<any[]>([])
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const {toggleCrispChat} = useCrispChat()
  const [totalGDPR, setTotalGDPR] = useState(0)
  const [healthyGDPRControlsCount, setHealthyGDPRControlsCount] = useState(0)
  const [totalHIPAA, setTotalHIPAA] = useState(0)
  const [healthyHIPAAControlsCount, setHealthyHIPAAControlsCount] = useState(0)

  useEffect(() => {
    getControls()
  }, [activeFrameworks])

  useEffect(() => {
    getActiveFramworks()
  }, [])

  const getActiveFramworks = () => {
    try {
      if (currentUser?.role !== 'Auditor') {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)

        const testsCollectionRef = collection(tenantRef, 'active_frameworks')

        const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
          const frameworksData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            frameworks: doc.data().frameworks,
          }))
          const frameworks = frameworksData[0]?.frameworks || []
          setActiveFrameworks(frameworks)
        })

        return unsubscribe
      } else {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)

        const testsCollectionRef = collection(tenantRef, 'audits')

        const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
          const frameworksData = snapshot.docs
            .map((doc) => ({
              ...doc.data(),
              id: doc.id,
              frameworks: doc.data().framework,
              auditor: doc.data().auditor, // Keep the full auditor array
              status: doc.data().status,
            }))
            .filter((item) => {
              if (!item.auditor) return false
              
              // Handle both array and single auditor cases
              if (Array.isArray(item.auditor)) {
                // Check if current user is in the auditors array by ID or email
                return item.auditor.some(auditor => 
                  auditor.id === currentUser?.uid || auditor.email === currentUser?.email
                ) && item.status !== 'Completed'
              } else {
                // Handle single auditor case
                return (item.auditor.id === currentUser?.uid || item.auditor.email === currentUser?.email) && item.status !== 'Completed'
              }
            })
          const frameworks = frameworksData[0]?.frameworks || []
          setActiveFrameworks(frameworks)
        })

        return unsubscribe
      }
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  const getControls = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'controls')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().control_framework_mappings,
          status: doc.data().control_evidence_status,
        }))

        const filteredControls = controlsData.filter((control) => {
          if (!control.framework) return false

          return activeFrameworks.some(
            (framework) =>
              control.framework.startsWith(framework) &&
              !control.framework.startsWith('ISO 27001:2013')
          )
        })

        setControls(filteredControls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    const iso27001Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('ISO')
    })

    const iso27001ControlsCount = iso27001Controls.length
    setTotalISO(iso27001ControlsCount)

    const SOC2Controls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.startsWith('SOC')
    })

    const SOC2ControlsCount = SOC2Controls.length
    setTotalSOC(SOC2ControlsCount)

    const GDPRControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('GDPR')
    })

    const GDPRControlsCount = GDPRControls.length
    setTotalGDPR(GDPRControlsCount)

    const HIPAAControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings
      return framework && framework.includes('HIPAA')
    })

    const HIPAAControlsCount = HIPAAControls.length
    setTotalHIPAA(HIPAAControlsCount)
  }, [controls])

  useEffect(() => {
    if (!controls || controls.length === 0) {
      return
    }

    const isoHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('ISO') && status.toLowerCase() === 'ok'
    })

    const isoHealthyControlsCount = isoHealthyControls.length

    setHealthyISOControlsCount(isoHealthyControlsCount)

    const socHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('SOC') && status.toLowerCase() === 'ok'
    })

    const socHealthyControlsCount = socHealthyControls.length

    setHealthySOCControlsCount(socHealthyControlsCount)

    const gdprHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('GDPR') && status.toLowerCase() === 'ok'
    })

    const gdprHealthyControlsCount = gdprHealthyControls.length
    setHealthyGDPRControlsCount(gdprHealthyControlsCount)

    const HIPAAHealthyControls = controls.filter((doc) => {
      const framework = doc.control_framework_mappings || ''
      const status = doc.control_evidence_status || ''
      return framework.includes('HIPAA') && status.toLowerCase() === 'ok'
    })

    const HIPAAHealthyControlsCount = HIPAAHealthyControls.length
    setHealthyHIPAAControlsCount(HIPAAHealthyControlsCount)
  }, [controls])

  return (
    <>
      <Helmet>
        <title>SecureSlate | Framework</title>
      </Helmet>
      <div className='row gx-8 gy-1 '>
        {activeFrameworks.includes('ISO 27001:2022') ? (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column mb-0`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/ISO.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4'>
                  <h2 className=''>ISO 27001:2022</h2>
                  <p
                    className='fs-6 p-0 text-wrap mb-4'
                    style={{color: '#5A5A5A', maxHeight: '3rem'}}
                  >
                    Your progress towards ISO 27001:2022 compliance based on your controls and
                    tests.
                  </p>

                  <div className='row align-items-center p-0 my-4'>
                    <div className='col-auto mx-3 p-0 mb-3 mb-lg-0 mt-8'>
                      <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                        <CircularProgressbar
                          value={(healthyISOControlsCount / totalISO) * 100}
                          // text={`${percentage}%`}
                          strokeWidth={15}
                          styles={buildStyles({
                            strokeLinecap: 'round',
                            pathTransitionDuration: 0.5,
                            pathColor: `#0E9F6E`,
                            textColor: '#000000',
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                          })}
                        />
                      </div>
                    </div>

                    <div
                      className='col fw-semibold p-0 mb-3 mb-lg-0 mt-8'
                      style={{color: '#171717'}}
                    >
                      {' '}
                      Controls OK:{' '}
                      {totalISO !== 0 ? (
                        `${((healthyISOControlsCount / totalISO) * 100).toFixed(0)}%`
                      ) : (
                        <span className='fs-7 fw-semibold' style={{color: '5A5A5A'}}>
                          Loading...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{height: '40px'}}>
                  <Link to={'/frameworks/details/ISO 27001:2022'} target='_blank'>
                    <Button btnType='primary' className='w-100'>
                      View Framework
                    </Button>
                  </Link>
                  {/* <a
                    href='/frameworks/details/ISO 27001:2022'
                    target='_blank'
                    rel='noopener noreferrer'
                    className={`btn w-100 `}
                    style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#148363')
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#0E9F6E')
                    }
                  >
                    View Framework
                  </a> */}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column mb-0`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/ISO.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4'>
                  <h2 className=''>ISO 27001:2022</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards ISO 27001:2022 compliance based on your controls and
                    tests.
                  </p>
                  <div className='row align-items-center p-0 my-4'>
                    <div
                      className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                      style={{color: '#171717'}}
                    >
                      Contact sales to enable this framework
                    </div>
                  </div>
                </div>
                <Button className='w-100' onClick={() => toggleCrispChat()}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                    <path d='M3 7l9 6l9 -6' />
                  </svg>
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeFrameworks.includes('SOC 2') ? (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/SOC.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4 '>
                  <h2 className=''>SOC 2</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards SOC 2 compliance based on your controls and tests.
                  </p>

                  <div className='row align-items-center p-0  my-4'>
                    <div className='col-auto mx-3 p-0 mb-3 mb-lg-0 mt-8'>
                      <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                        <CircularProgressbar
                          value={(healthySOCControlsCount / totalSOC) * 100}
                          // text={`${percentage}%`}
                          strokeWidth={15}
                          styles={buildStyles({
                            strokeLinecap: 'round',
                            pathTransitionDuration: 0.5,
                            pathColor: `#0E9F6E`,
                            textColor: '#000000',
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                          })}
                        />
                      </div>
                    </div>

                    <div
                      className='col fw-semibold p-0 mb-4 mt-4 mb-lg-0 mt-8'
                      style={{color: '#171717'}}
                    >
                      {' '}
                      Controls OK:{' '}
                      {totalSOC !== 0
                        ? `${((healthySOCControlsCount / totalSOC) * 100).toFixed(0)}%`
                        : 'Loading...'}
                    </div>
                  </div>
                </div>
                <div style={{height: '40px'}}>
                  <Link to={'/frameworks/details/SOC_2'} target='_blank'>
                    <Button btnType='primary' className='w-100'>
                      View Framework
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/SOC.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4'>
                  <h2 className=''>SOC 2</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards SOC 2 compliance based on your controls and tests.
                  </p>
                  <div className='row align-items-center p-0 my-4'>
                    <div
                      className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                      style={{color: '#171717'}}
                    >
                      Contact sales to enable this framework
                    </div>
                  </div>
                </div>
                <Button className='w-100' onClick={() => toggleCrispChat()}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                    <path d='M3 7l9 6l9 -6' />
                  </svg>
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        )}
        {activeFrameworks.includes('GDPR') ? (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/GDPRFramework.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4 '>
                  <h2 className=''>GDPR</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards GDPR compliance based on your controls and tests.
                  </p>

                  <div className='row align-items-center p-0  my-4'>
                    <div className='col-auto mx-3 p-0 mb-3 mb-lg-0 mt-8'>
                      <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                        <CircularProgressbar
                          value={(healthyGDPRControlsCount / totalGDPR) * 100}
                          strokeWidth={15}
                          styles={buildStyles({
                            strokeLinecap: 'round',
                            pathTransitionDuration: 0.5,
                            pathColor: `#0E9F6E`,
                            textColor: '#000000',
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                          })}
                        />
                      </div>
                    </div>

                    <div
                      className='col fw-semibold p-0 mb-4 mt-4 mb-lg-0 mt-8'
                      style={{color: '#171717'}}
                    >
                      {' '}
                      Controls OK:{' '}
                      {totalGDPR !== 0
                        ? `${((healthyGDPRControlsCount / totalGDPR) * 100).toFixed(0)}%`
                        : 'Loading...'}
                    </div>
                  </div>
                </div>
                <div style={{height: '40px'}}>
                  <Link to={'/frameworks/details/GDPR'} target='_blank'>
                    <Button btnType='primary' className='w-100'>
                      View Framework
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/GDPRFramework.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4'>
                  <h2 className=''>GDPR</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards GDPR compliance based on your controls and tests.
                  </p>
                  <div className='row align-items-center p-0 my-4'>
                    <div
                      className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                      style={{color: '#171717'}}
                    >
                      Contact sales to enable this framework
                    </div>
                  </div>
                </div>
                <Button className='w-100' onClick={() => toggleCrispChat()}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                    <path d='M3 7l9 6l9 -6' />
                  </svg>
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
          <div className={`card ${className}  h-90 d-flex flex-column`}>
            <div className='card-body flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart pt-1'>
                  <img src='/media/logos/ISO.svg' className='img-fluid' alt='' />
                </div>
              </div>

              <div className='mt-4'>
                <h2 className=''>ISO 9001</h2>
                <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                  Your progress towards ISO 9001 compliance based on your controls and tests.
                </p>
                <div className='row align-items-center p-0 my-4'>
                  {/* <div className='col-auto mx-2 p-0 mb-3 mb-lg-0'>
                    <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                      <CircularProgressbar
                        value={percentage}
                        // text={`${percentage}%`}
                        strokeWidth={15}
                        styles={buildStyles({
                          strokeLinecap: 'round',
                          pathTransitionDuration: 0.5,
                          pathColor: `#44dbae`,
                          textColor: '#000000',
                          trailColor: '#d6d6d6',
                          backgroundColor: '#3e98c7',
                        })}
                      />
                    </div>
                  </div>

                  <div className='col text-muted p-0 mb-3 mb-lg-0'> Controls Ok: {percentage}%</div>
                </div> */}

                  <div
                    className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                    style={{color: '#171717'}}
                  >
                    Contact sales to enable this framework
                  </div>
                </div>
              </div>
              <Button className='w-100' onClick={() => toggleCrispChat()}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                  <path d='M3 7l9 6l9 -6' />
                </svg>
                Contact Sales
              </Button>
            </div>
          </div>
        </div>

        {activeFrameworks.includes('HIPAA') ? (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/HIPPAFramework.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4 '>
                  <h2 className=''>HIPAA</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards HIPAA compliance based on your controls and tests.
                  </p>

                  <div className='row align-items-center p-0  my-4'>
                    <div className='col-auto mx-3 p-0 mb-3 mb-lg-0 mt-8'>
                      <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                        <CircularProgressbar
                          value={(healthyHIPAAControlsCount / totalHIPAA) * 100}
                          strokeWidth={15}
                          styles={buildStyles({
                            strokeLinecap: 'round',
                            pathTransitionDuration: 0.5,
                            pathColor: `#0E9F6E`,
                            textColor: '#000000',
                            trailColor: '#d6d6d6',
                            backgroundColor: '#3e98c7',
                          })}
                        />
                      </div>
                    </div>

                    <div
                      className='col fw-semibold p-0 mb-4 mt-4 mb-lg-0 mt-8'
                      style={{color: '#171717'}}
                    >
                      {' '}
                      Controls OK:{' '}
                      {totalHIPAA !== 0
                        ? `${((healthyHIPAAControlsCount / totalHIPAA) * 100).toFixed(0)}%`
                        : 'Loading...'}
                    </div>
                  </div>
                </div>
                <div style={{height: '40px'}}>
                  <Link to={'/frameworks/details/HIPAA'} target='_blank'>
                    <Button btnType='primary' className='w-100'>
                      View Framework
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
            <div className={`card ${className}  h-90 d-flex flex-column`}>
              <div className='card-body flex-column'>
                <div className='flex-grow-1'>
                  <div className='mixed-widget-4-chart pt-1'>
                    <img src='/media/logos/HIPPAFramework.svg' className='img-fluid' alt='' />
                  </div>
                </div>

                <div className='mt-4'>
                  <h2 className=''>HIPAA</h2>
                  <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                    Your progress towards HIPAA compliance based on your controls and tests.
                  </p>
                  <div className='row align-items-center p-0 my-4'>
                    <div
                      className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                      style={{color: '#171717'}}
                    >
                      Contact sales to enable this framework
                    </div>
                  </div>
                </div>
                <Button className='w-100' onClick={() => toggleCrispChat()}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                    <path d='M3 7l9 6l9 -6' />
                  </svg>
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
          <div className={`card ${className}  h-90 d-flex flex-column`}>
            <div className='card-body flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart pt-1'>
                  <img src='/media/logos/PCIDSS.svg' className='img-fluid' alt='' />
                </div>
              </div>

              <div className='mt-4'>
                <h2 className=''>PCI DSS</h2>
                <p className='fs-6 p-0 text-wrap' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                  Your progress towards PCI DSS compliance based on your controls and tests.
                </p>
                <div className='row align-items-center p-0 my-4'>
                  {/* <div className='col-auto mx-2 p-0 mb-3 mb-lg-0'>
                    <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                      <CircularProgressbar
                        value={percentage}
                        // text={`${percentage}%`}
                        strokeWidth={15}
                        styles={buildStyles({
                          strokeLinecap: 'round',
                          pathTransitionDuration: 0.5,
                          pathColor: `#44dbae`,
                          textColor: '#000000',
                          trailColor: '#d6d6d6',
                          backgroundColor: '#3e98c7',
                        })}
                      />
                    </div>
                  </div>

                  <div className='col text-muted p-0 mb-3 mb-lg-0'> Controls Ok: {percentage}%</div>
                </div> */}

                  <div
                    className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                    style={{color: '#171717'}}
                  >
                    Contact sales to enable this framework
                  </div>
                </div>
              </div>
              <Button className='w-100' onClick={() => toggleCrispChat()}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                  <path d='M3 7l9 6l9 -6' />
                </svg>
                Contact Sales
              </Button>
            </div>
          </div>
        </div>

        <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12'>
          <div className={`card ${className}  h-90 d-flex flex-column`}>
            <div className='card-body flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart pt-1'>
                  <img src='/media/logos/CCPAFramework.svg' className='img-fluid' alt='' />
                </div>
              </div>

              <div className='mt-4'>
                <h2 className=''>CCPA</h2>
                <p className='fs-6 p-0 text-wrap ' style={{color: '#5A5A5A', maxHeight: '3rem'}}>
                  Your progress towards CCPA compliance based on your controls and tests.
                </p>
                <div className='row align-items-center p-0 my-4'>
                  {/* <div className='col-auto mx-2 p-0 mb-3 mb-lg-0'>
                    <div style={{maxWidth: '20px', maxHeight: '100px'}}>
                      <CircularProgressbar
                        value={percentage}
                        // text={`${percentage}%`}
                        strokeWidth={15}
                        styles={buildStyles({
                          strokeLinecap: 'round',
                          pathTransitionDuration: 0.5,
                          pathColor: `#44dbae`,
                          textColor: '#000000',
                          trailColor: '#d6d6d6',
                          backgroundColor: '#3e98c7',
                        })}
                      />
                    </div>
                  </div>

                  <div className='col text-muted p-0 mb-3 mb-lg-0'> Controls Ok: {percentage}%</div>
                </div> */}

                  <div
                    className='col p-0 mb-3 mx-3 mb-lg-0 fw-semibold mt-8'
                    style={{color: '#171717'}}
                  >
                    Contact sales to enable this framework
                  </div>
                </div>
              </div>
              <Button className='w-100' onClick={() => toggleCrispChat()}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-mail mb-1 me-2'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                  <path d='M3 7l9 6l9 -6' />
                </svg>
                Contact Sales
              </Button>
            </div>
          </div>
        </div>

        {/* <div className='col-xl-4'>
          <div className={`card ${className}`}>
            <div className='card-body d-flex justify-content-center text-center flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart'>
                  <img src='/media/logos/ISO 27001.svg' alt='' />
                </div>
              </div>

              <div className='pt-5'>
                <h3 className='text-center fs-6 pb-5 '>ISO 27001 Readiness Assessment</h3>

                <a
                  href='#'
                  className={`btn w-100 py-3`}
                  style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
                >
                  View Report
                </a>
              </div>
            </div>
          </div>
        </div> */}
        {/* <div className='col-xl-4'>
          <div className={`card ${className}`}>
            <div className='card-body d-flex justify-content-center text-center flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart'>
                  <img src='/media/logos/HIPAA.svg'alt='' />
                </div>
              </div>

              <div className='pt-5'>
                <h3 className='text-center fs-6 pb-5 '>HIPAA Readiness Assessment</h3>

                <a
                  href='#'
                  className={`btn w-100 py-3`}
                  style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
                >
                  View Report
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className='col-xl-4'>
          <div className={`card ${className}`}>
            <div className='card-body d-flex justify-content-center text-center flex-column'>
              <div className='flex-grow-1'>
                <div className='mixed-widget-4-chart'>
                  <img src='/media/logos/GDPR.svg' alt='' />
                </div>
              </div>

              <div className='pt-5'>
                <h3 className='text-center fs-6 pb-5 '>GDPR Readiness Assessment</h3>

                <a
                  href='#'
                  className={`btn w-100 py-3`}
                  style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
                >
                  View Report
                </a>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </>
  )
}

export {Reports}
